import { Injectable, inject, signal } from '@angular/core';
import { StorageService } from '../storage/storage.service';

export interface RecentSearch {
  name: string;
  image?: string;
}

/**
 * Las búsquedas recientes del buscador.
 *
 * Estaban implementadas DOS VECES, carácter a carácter, en `search` y en `top-searches`:
 * el mismo `saveInRecientes` de veinte líneas (con su control de duplicados y su tope de
 * cinco) y el mismo `getRecientes`. Al vivir en dos sitios, cada uno tenía su propia copia
 * de la lista y podían quedarse desincronizadas: se guardaba en uno y el otro seguía
 * enseñando lo de antes hasta recargar.
 */
@Injectable({ providedIn: 'root' })
export class RecentSearchesService {
  private storage = inject(StorageService);

  private static readonly CLAVE = 'recientes';
  private static readonly MAXIMO = 5;

  /** Señal para que la lista se repinte sola en quien la esté enseñando. */
  readonly list = signal<RecentSearch[]>([]);

  async load(): Promise<RecentSearch[]> {
    try {
      const guardadas = await this.storage.getItem(RecentSearchesService.CLAVE);
      if (guardadas) this.list.set(JSON.parse(guardadas));
    } catch (error) {
      console.error(error);
    }
    return this.list();
  }

  async add(tag: RecentSearch) {
    if (!tag?.name) return;

    await this.load();
    if (this.list().some(e => e.name === tag.name)) return;

    const nuevas = [...this.list().filter(e => e.name), { name: tag.name, image: tag.image }]
      .slice(-RecentSearchesService.MAXIMO);

    await this.guardar(nuevas);
  }

  async remove(name: string) {
    await this.guardar(this.list().filter(e => e.name !== name));
  }

  async clear() {
    this.list.set([]);
    try {
      await this.storage.deleteItem(RecentSearchesService.CLAVE);
    } catch (error) {
      console.error(error);
    }
  }

  private async guardar(lista: RecentSearch[]) {
    this.list.set(lista);
    try {
      await this.storage.setItem(RecentSearchesService.CLAVE, JSON.stringify(lista));
    } catch (error) {
      console.error(error);
    }
  }
}
